import { IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';

/**
 * REV-20899 (reclass save-side isolation).
 *
 * The reclass "bridge" (`applyReclassificationsToFinStatements`) produces a
 * DISPLAY-only view of the financial statements: it zeroes every account's
 * `openingReClass` / `closingReClass` and re-derives them from the
 * transactional reclassifications. That view must NEVER be persisted, because
 * saving it overwrites a v1 report's inherited legacy reclasses with the
 * derived values and destroys the frozen reference the old "Adjustments &
 * Reclasses" tab reads.
 *
 * Save paths therefore source the report from the RAW selectors
 * (`getActiveAnnualReport$`), never the materialized ones
 * (`getActiveAnnualReportWithReclasses$` / `getActiveFinStatementsWithReclasses$`).
 * To make a future regression impossible to introduce silently, the provider
 * tags every bridged `IFinStatements` with a marker and the single save funnel
 * refuses to persist a tagged object (see {@link assertPersistableFinStatements}).
 *
 * The marker is a `Symbol`-keyed property, which makes it:
 * - invisible to `JSON.stringify` (never sent to the backend, never persisted),
 * - invisible to `Object.keys`-based deep equality (dirty checks unaffected),
 * - absent from the persisted `IFinStatements` type (no backend type release).
 *
 * It is written as a normal (enumerable) property so it survives object spread
 * (`{ ...fs }`); that lets the guard also catch the case where a component
 * rebuilds `finStatements` from a bridged source before saving.
 */
const RECLASS_MATERIALIZED_MARKER = Symbol('aumico.reclassMaterialized');

type ReclassMaterializedTagged = IFinStatements & { [RECLASS_MATERIALIZED_MARKER]?: true };

/** Tag a bridged (display-only) fin statement so it can never be persisted. */
export function markReclassMaterialized(finStatement: IFinStatements): void {
    (finStatement as ReclassMaterializedTagged)[RECLASS_MATERIALIZED_MARKER] = true;
}

/** Whether a fin statement carries the bridged (display-only) marker. */
export function isReclassMaterialized(finStatement: IFinStatements | null | undefined): boolean {
    return finStatement != null && (finStatement as ReclassMaterializedTagged)[RECLASS_MATERIALIZED_MARKER] === true;
}

/**
 * Save-boundary guard: throws if any fin statement in the payload is a bridged
 * (display-only) view. A hit always means a real bug (a save path is persisting
 * the reclass-materialized object instead of the raw one), so we fail loudly
 * rather than silently corrupt data.
 */
export function assertPersistableFinStatements(finStatements: IFinStatements[] | null | undefined): void {
    if (Array.isArray(finStatements) && finStatements.some(isReclassMaterialized)) {
        throw new Error(
            'Refusing to persist reclass-materialized (bridged) finStatements. A save must source ' +
                'the annual report from getActiveAnnualReport$ (raw), not from ' +
                'getActiveAnnualReportWithReclasses$ / getActiveFinStatementsWithReclasses$. ' +
                'See REV-20899 (reclass save-side isolation).',
        );
    }
}
